import { z } from "zod";
import type { MeasurementReading } from "./materials";
import { type MeasureItemDef, lineMatchesItem } from "./measure-items";

/**
 * Roof measurements from a carrier's estimate (Xactimate and the like). The reader returns each roof
 * area's own measurement summary ("Surface Area, Number of Squares, Total Perimeter Length, Total
 * Ridge Length, Total Hip Length...") and that area's lines; the rest is worked out here, in plain
 * rules: the waste from the shingle line, valleys from valley metal, flashing, and the company's
 * extra items (pipe jacks, vents...) from their lines.
 */

export interface ScopeRoofArea {
  name: string;
  surfaceAreaSqFt: number;
  squares: number;
  perimeterFt: number;
  ridgeFt: number;
  hipFt: number;
  valleyFt: number;
  eaveFt: number;
  rakeFt: number;
  /** "Auto Calculated Waste: 8.1%" when the scope prints it. */
  wastePercent: number | null;
}
export interface ScopeLine {
  n: number | null;
  area: string;
  description: string;
  quantity: number;
  unit: string;
}
export interface ScopeReading {
  roofAreas: ScopeRoofArea[];
  lines: ScopeLine[];
}

const n = z.coerce.number().min(0).max(10_000_000).catch(0);
export const scopeReadingSchema = z.object({
  roofAreas: z
    .array(
      z.object({
        name: z.string().trim().max(120).catch(""),
        surfaceAreaSqFt: n,
        squares: n,
        perimeterFt: n,
        ridgeFt: n,
        hipFt: n,
        valleyFt: n,
        eaveFt: n,
        rakeFt: n,
        wastePercent: z.coerce.number().min(0).max(50).nullable().catch(null),
      }),
    )
    .max(20)
    .catch([]),
  lines: z
    .array(z.object({ n: z.coerce.number().int().nullable().catch(null), area: z.string().trim().max(120).catch(""), description: z.string().trim().max(400).catch(""), quantity: n, unit: z.string().trim().max(10).catch("") }))
    .max(400)
    .catch([]),
});

export const SCOPE_MEASURE_TOOL_NAME = "record_roof_measurements";
export const SCOPE_MEASURE_PROMPT = `This is an insurance carrier's estimate (Xactimate or similar). Find the ROOF areas only (a section whose summary block lists "Surface Area", "Number of Squares", "Total Perimeter Length", "Total Ridge Length", "Total Hip Length" and is a roof, e.g. "Dwelling Roof", "Detached Garage Roof", "Detached - Shed"). Skip elevations, interiors, gutters-only and fence areas.
For each roof area give its own summary numbers exactly as printed: surfaceAreaSqFt, squares, perimeterFt, ridgeFt, hipFt, and valleyFt, eaveFt, rakeFt only if the summary prints them (else 0). wastePercent: the area's "Auto Calculated Waste" (or "waste") percent if printed, else null.
Then list every line item in those roof areas: n (the line number), area (the roof area's name), description (as printed), quantity (number), unit (SQ, LF, EA, SF...).
Call ${SCOPE_MEASURE_TOOL_NAME} with roofAreas and lines.`;

export function scopeMeasureToolSchema() {
  const num = { type: "number" };
  return {
    type: "object",
    properties: {
      roofAreas: {
        type: "array",
        items: {
          type: "object",
          properties: { name: { type: "string" }, surfaceAreaSqFt: num, squares: num, perimeterFt: num, ridgeFt: num, hipFt: num, valleyFt: num, eaveFt: num, rakeFt: num, wastePercent: { type: ["number", "null"] } },
          required: ["name", "surfaceAreaSqFt", "squares", "perimeterFt", "ridgeFt", "hipFt"],
        },
      },
      lines: {
        type: "array",
        items: { type: "object", properties: { n: { type: ["integer", "null"] }, area: { type: "string" }, description: { type: "string" }, quantity: num, unit: { type: "string" } }, required: ["area", "description", "quantity", "unit"] },
      },
    },
    required: ["roofAreas", "lines"],
  };
}

const ft = (lines: ScopeLine[], test: RegExp, not?: RegExp) => lines.filter((l) => /^(lf|ft)$/i.test(l.unit) && test.test(l.description) && !(not && not.test(l.description)));
const sum = (ls: ScopeLine[]) => ls.reduce((s, l) => s + l.quantity, 0);
const where = (ls: ScopeLine[]) => ls.map((l) => `${l.n !== null ? `Line ${l.n}: ` : ""}${l.description} ${l.quantity} ${l.unit}`).join("; ").slice(0, 300);
const up = (x: number) => (x > 0 ? Math.round(x * 100) / 100 : 0);

/** One measurement per roof area, the biggest first (the main roof). */
export function measurementsFromScope(scope: ScopeReading, items: readonly MeasureItemDef[], defaultWastePercent: number): { label: string; reading: MeasurementReading; wastePercent: number }[] {
  const areas = scope.roofAreas.filter((a) => a.surfaceAreaSqFt > 0 || a.squares > 0).sort((a, b) => (b.surfaceAreaSqFt || b.squares * 100) - (a.surfaceAreaSqFt || a.squares * 100));
  const main = areas[0]?.name ?? "";
  return areas.map((a) => {
    // A line names its roof area; one that doesn't is the main roof's.
    const lines = scope.lines.filter((l) => (l.area ? l.area.toLowerCase() === a.name.toLowerCase() : a.name === main));
    const sources: Record<string, string> = {};
    const areaSqFt = a.surfaceAreaSqFt || a.squares * 100;
    sources.totalAreaSqFt = "Scope roof summary";
    // Waste: printed, else the shingle line against the measured squares.
    const shingle = lines.find((l) => /^sq$/i.test(l.unit) && /shingle|shake|slate|tile/i.test(l.description) && !/remove|tear|underlay|hip|ridge|starter|detach/i.test(l.description));
    let waste = a.wastePercent !== null ? Math.round(a.wastePercent) : null;
    if (waste === null && shingle && areaSqFt > 0) waste = Math.max(0, Math.round((shingle.quantity / (areaSqFt / 100) - 1) * 100));
    if (shingle) sources.wastePercent = where([shingle]);
    let valleyFt = a.valleyFt;
    if (valleyFt) sources.valleyFt = "Scope roof summary";
    else {
      const v = ft(lines, /valley/i);
      valleyFt = sum(v);
      if (v.length) sources.valleyFt = where(v);
    }
    // Eaves and rakes when printed; otherwise the perimeter, kept on the eaves.
    let eaveFt = a.eaveFt;
    let rakeFt = a.rakeFt;
    if (!eaveFt && !rakeFt && a.perimeterFt) {
      eaveFt = a.perimeterFt;
      sources.eaveFt = "Scope roof summary: total perimeter (eaves + rakes, not split)";
    }
    const step = ft(lines, /step flash/i);
    const wall = ft(lines, /counter.?flash|apron|headwall|wall flash/i);
    if (step.length) sources.stepFlashingFt = where(step);
    if (wall.length) sources.wallFlashingFt = where(wall);
    const extra = items.flatMap((d) => {
      const hit = lines.filter((l) => lineMatchesItem(l.description, d) && (d.unit === "ft" ? /^(lf|ft)$/i.test(l.unit) : !/^(lf|ft|sf|sq)$/i.test(l.unit)));
      if (!hit.length) return [];
      sources[`item:${d.key}`] = where(hit);
      return [{ key: d.key, quantity: Math.round(sum(hit) * 100) / 100 }];
    });
    const reading: MeasurementReading = {
      source: "other",
      address: "",
      totalAreaSqFt: up(areaSqFt),
      pitches: [],
      facets: null,
      ridgeFt: up(a.ridgeFt),
      hipFt: up(a.hipFt),
      valleyFt: up(valleyFt),
      eaveFt: up(eaveFt),
      rakeFt: up(rakeFt),
      stepFlashingFt: up(sum(step)),
      wallFlashingFt: up(sum(wall)),
      suggestedWastePercent: waste,
      items: extra,
      sources,
    };
    return { label: a.name, reading, wastePercent: waste ?? defaultWastePercent };
  });
}
